/**
 * ENG-5565: run-boundary marker injected into the agent's REPL alongside
 * manager-injected work, so per-injection token usage can later be attributed
 * to a run (and thence to a scheduled task / kanban card).
 *
 * The marker is delivered as a plain user turn (via tmux send-keys — see
 * `maybeInjectKanbanCheck` in apps/cli manager-worker) and is inert: an
 * HTML-style comment the agent ignores. The transcript per-turn parser
 * (ENG-5566) reads these markers to delimit which assistant turns belong to
 * which run.
 *
 * NOT for slash-command injects: a slash command sent via send-keys is parsed
 * by the REPL's keystroke-layer slash parser, so it carries NO marker (the
 * manager falls back to a time-bracket for those).
 */

/** Render the inert run-boundary marker line for `runId`. */
export function formatRunMarker(runId: string): string {
  return `<!-- agt-run:${runId} -->`;
}

/**
 * Matches a run-boundary marker and captures the run id (UUID v4 shape).
 * Used by the per-turn transcript parser to find injection boundaries inside
 * a shared persistent session's transcript.
 */
export const RUN_MARKER_RE =
  /<!--\s*agt-run:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\s*-->/;

/**
 * Global-flagged twin of RUN_MARKER_RE, built from its `.source` so the two can
 * never drift. Only ever used with `String.replace`, which resets `lastIndex`
 * on every call - do not `.test()` with it (a shared /g regex carries state).
 */
const RUN_MARKER_RE_GLOBAL = new RegExp(RUN_MARKER_RE.source, 'g');

/**
 * Depth bound for `findRunMarker`. A transcript record is attacker-adjacent —
 * tool results carry whatever a tool returned — so the walk is bounded rather
 * than trusting the shape. 6 clears every real nesting seen (record.message
 * .content[] -> tool_result.content[] -> {text}) with room to spare.
 */
const MARKER_WALK_MAX_DEPTH = 6;

/** Node budget for one `findRunMarker` walk, so a pathological blob can't stall the scan. */
const MARKER_WALK_MAX_NODES = 512;

/**
 * Find the run-boundary marker inside a transcript record's `message.content`,
 * whatever shape that content takes. Returns the LAST marker in walk order (the
 * most recent boundary wins when one record somehow carries two), or null.
 *
 * WHY THIS IS NOT `userTurnText().match()` (ENG-8832). `attributeTranscriptUsageByRun`
 * reads only the `text` field of top-level content blocks, which is where the
 * marker lands when a nudge is delivered by tmux send-keys. The kanban and
 * scheduled-task lanes no longer use send-keys — ENG-7967/ENG-8743 moved them to
 * the durable direct-chat notice rail, where the agent receives the nudge as a
 * `tool_result` and the marker sits one level deeper, under `content` rather
 * than `text`. A finder that only reads the top level would see no markers at
 * all on those lanes and attribute every call to the unattributed bucket — a
 * silent, total loss of exactly the attribution this exists to provide.
 *
 * FORGERY BOUND, STATED PLAINLY. A `tool_result` carries whatever the tool
 * returned, so an agent that reads a file containing a marker can open a scope
 * with it. That is contained, not prevented: `POST /host/tool-calls` stores
 * `run_id` only after confirming the run belongs to the SAME agent, so the worst
 * available outcome is an agent re-attributing its own work between its own
 * runs. It cannot reach another agent's, another team's, or another org's. This
 * is an index into the transcript, and the transcript remains the evidence.
 */
export function findRunMarker(content: unknown): string | null {
  let found: string | null = null;
  let nodes = 0;

  const walk = (value: unknown, depth: number): void => {
    if (depth > MARKER_WALK_MAX_DEPTH || nodes >= MARKER_WALK_MAX_NODES) return;
    nodes += 1;

    if (typeof value === 'string') {
      // Last match in the string wins, mirroring last-marker-wins across nodes.
      const matches = value.match(RUN_MARKER_RE_GLOBAL);
      const last = matches?.[matches.length - 1];
      if (last) {
        const id = last.match(RUN_MARKER_RE)?.[1];
        if (id) found = id;
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) walk(item, depth + 1);
      return;
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      // Only the two fields that actually carry rendered text. Walking every key
      // would reach tool INPUTS, which is content this module must never read.
      walk(obj.text, depth + 1);
      walk(obj.content, depth + 1);
    }
  };

  walk(content, 0);
  return found;
}

/**
 * Remove every run-boundary marker from `text`, for display to a human.
 *
 * ENG-8294. The marker is "inert" only where the consumer treats it as markup:
 * the agent's REPL ignores it, and any markdown renderer swallows an HTML
 * comment. Direct Chat notices are deliberately rendered as PLAIN TEXT (they are
 * system FYIs, not agent prose), so there the marker is just characters and gets
 * drawn on screen - a user sees `<!-- agt-run:6efee62d-... -->` glued to the end
 * of a scheduled-task notice.
 *
 * This is a DISPLAY-ONLY transform. It must never be applied on the path that
 * feeds the agent or the transcript: the marker is exactly what ENG-5566's
 * per-turn parser uses to attribute a run's token usage, so stripping it at
 * write time would silently break run/token attribution. Call it at the render
 * boundary, where the audience is a person.
 *
 * The marker is appended on its own last line, so removing it leaves a dangling
 * trailing newline; trailing whitespace goes with it. Interior text is left
 * alone. The cheap `includes` guard is not just for speed - it keeps the
 * trailing-whitespace trim scoped to strings that actually carried a marker, so
 * a notice without one renders byte-for-byte as it does today.
 */
export function stripRunMarkers(text: string): string {
  if (!text.includes('agt-run:')) return text;
  return text.replace(RUN_MARKER_RE_GLOBAL, '').replace(/\s+$/, '');
}
